import { mapGetters, mapState, mapActions } from 'vuex';
import throttle from 'lodash/throttle';

import ScanProcess from '@/modules/scan-process';
import { EVENTS } from '../events/hardware';

const ACTIVE_TIME = 1500;

const throttledLog = throttle(message => {
  console.log(message);
}, 3000);

const handleArmDetection = {
  mounted() {
    this.leftTimer = null;
    this.rightTimer = null;
    this.startListen();
  },
  beforeDestroy() {
    this.cancelLeft();
    this.cancelRight();
    this.stopListen();
  },
  data: () => ({
    left: 0,
    right: 0,
    leftHit: false,
    rightHit: false,
    leftReady: false,
    rightReady: false,
    activeTime: ACTIVE_TIME,
    listenToArmMotion: true,
  }),
  methods: {
    ...mapActions('hardware', ['toggleArmDetection', 'setArmsRange']),
    toggleArmMotionListening(toListen) {
      console.log('toggleArmMotionListening', toListen);
      this.listenToArmMotion = toListen;
      if (!toListen) {
        this.resetDoll();
      }
    },
    resetDoll() {
      this.left = 0;
      this.right = 0;
      this.cancelLeft();
      this.cancelRight();
      ScanProcess.latestUpdateArmTime = 0;
    },
    handleLeftClick() {
      this.cancelRight();
      this.leftHit = true;
      this.leftReady = true;
    },
    handleRightClick() {
      this.cancelLeft();
      this.rightReady = true;
      this.rightHit = true;
    },
    handleLeftHit(isHit) {
      if (!isHit) {
        this.cancelLeft();
      } else if (!this.leftTimer) {
        this.leftHit = true;
        this.leftTimer = setTimeout(() => {
          this.leftReady = true;
        }, this.activeTime);
      }
    },
    handleRightHit(isHit) {
      if (!isHit) {
        this.cancelRight();
      } else if (!this.rightTimer) {
        this.rightHit = true;
        this.rightTimer = setTimeout(() => {
          this.rightReady = true;
        }, this.activeTime);
      }
    },
    cancelLeft() {
      this.leftHit = false;
      this.leftReady = false;
      if (this.leftTimer) {
        clearTimeout(this.leftTimer);
      }
      this.leftTimer = null;
    },
    cancelRight() {
      this.rightHit = false;
      this.rightReady = false;
      if (this.rightTimer) {
        clearTimeout(this.rightTimer);
      }
      this.rightTimer = null;
    },
    startArmDetection() {
      if (this.hasArmDetectionStarted) return;

      this.toggleArmDetection(true);
      this.$events.emit(EVENTS.START_ARM_DETECTION, this.scannerId);
    },
    stopArmDetection(scannerId, unsubscribeOnly) {
      if (!this.hasArmDetectionStarted) return;

      // pass an empty string so the native would only unsubscribe for BT data
      // but not disconnect from device
      this.$events.emit(
        EVENTS.STOP_ARM_DETECTION,
        unsubscribeOnly ? '' : scannerId || this.scannerId
      );
      this.toggleArmDetection(false);
    },
    startListen() {
      ScanProcess.latestUpdateArmTime = 0;
      console.log('start listening to bt data');
      this.$events.on(EVENTS.BT_DATA, this.handleArmDetection);
    },
    stopListen() {
      ScanProcess.latestUpdateArmTime = 0;
      console.log('stop listening to bt data');
      this.$events.off(EVENTS.BT_DATA, this.handleArmDetection);
    },
    handleArmDetection({ data }) {
      if (!Array.isArray(data) || data.length < 2) {
        console.log('handleArmDetection: corrupted data', data);
        return;
      }

      throttledLog(`[BT_DATA]: ${JSON.stringify(data)}`);
      ScanProcess.latestUpdateArmTime = Date.now();
      if (data.length === 4) {
        this.setArmsRange(data);
      } else if (this.listenToArmMotion) {
        this.updateLeftRight(data);
      }
    },
    updateLeftRight(data) {
      const [left, right] = data;

      if (left > 0 && left <= 255) {
        this.left = left;
      }
      if (right > 0 && right <= 255) {
        this.right = right;
      }
    },
    putArmsInRange() {
      this.left = (this.leftRange[0] + this.leftRange[1]) / 2;
      this.right = (this.rightRange[0] + this.rightRange[1]) / 2;
    },
  },
  computed: {
    ...mapState('mtmUser', {
      mtmUserScannerId: state => state.fixedScannerId,
    }),
    ...mapState('hardware', {
      isBTReady: state => state.bluetooth.available,
      userScannerId: state => state.scannerId,
    }),
    ...mapGetters('hardware', ['leftRange', 'rightRange', 'hasArmDetectionStarted']),
    scannerId() {
      return this.userScannerId || this.mtmUserScannerId;
    },
    somethingHit() {
      return this.leftHit || this.rightHit;
    },
    bothHit() {
      return this.leftHit && this.rightHit;
    },
    leftHigh() {
      return this.left > this.leftRange[1];
    },
    leftLow() {
      return this.left < this.leftRange[0];
    },
    rightHigh() {
      return this.right > this.rightRange[1];
    },
    rightLow() {
      return this.right < this.rightRange[0];
    },
    bothHigh() {
      return this.leftHigh && this.rightHigh;
    },
    bothLow() {
      return this.leftLow && this.rightLow;
    },
  },
};

export default handleArmDetection;
